import { CssBaseline, ThemeProvider } from "@mui/material";
import { esES } from "@mui/material/locale";
import { createTheme, StyledEngineProvider } from "@mui/material/styles";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import localStorageUtils from "~/helpers/localStorageUtils";
import useSaveLocalPreference from "~/hooks/useSaveLocalPreference";
import { normalizeLanguage } from "~/i18n/languagePreference";
import AppThemeContext from "~/providers/AppThemeContext";
import { themeCreator } from "~/themes/pageThemes";

interface AppThemeProps {
	children: React.JSX.Element;
}

const AppThemeProvider = (props: AppThemeProps) => {
	// State to hold the selected theme name
	const { children } = props;

	const saveLocalPreference = useSaveLocalPreference();
	const userpref = localStorageUtils.readItem("themeType", "light") === "dark" ? "dark" : "light";
	const userprefSize = localStorageUtils.readItem("themeSize", "14");
	const [themeName, setThemeName] = useState("worksuite");
	const [themeType, setThemeTypePref] = useState(userpref);
	const [themeSize, setThemeSizePref] = useState(userprefSize);

	const { i18n } = useTranslation();
	const language = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

	const theme = useMemo(() => {
		const appTheme = themeCreator(themeName, themeType, themeSize);
		return language === "es" ? createTheme(appTheme, esES) : appTheme;
	}, [language, themeName, themeType, themeSize]);

	const setThemeType = useCallback(
		(type: string) => {
			if (!saveLocalPreference(() => localStorage.setItem("themeType", type))) return;
			setThemeTypePref(type);
		},
		[saveLocalPreference]
	);

	const setThemeSize = useCallback(
		(size: string) => {
			if (!saveLocalPreference(() => localStorage.setItem("themeSize", size))) return;
			setThemeSizePref(size);
		},
		[saveLocalPreference]
	);

	const contextValue = useMemo(
		() => ({
			setTheme: setThemeName,
			setThemeSize: setThemeSize,
			setThemeType: setThemeType,
			themeName: themeName,
			themeSize: themeSize,
			themeType: themeType
		}),
		[setThemeSize, setThemeType, themeName, themeSize, themeType]
	);

	return (
		<StyledEngineProvider injectFirst>
			<AppThemeContext.Provider value={contextValue}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					{children}
				</ThemeProvider>
			</AppThemeContext.Provider>
		</StyledEngineProvider>
	);
};

export default AppThemeProvider;
